import { useId, useState, type InputHTMLAttributes } from 'react';

import estilos from './CampoDeSenha.module.css';

/**
 * Campo de senha com botão para revelar o conteúdo.
 *
 * POR QUE O OLHO IMPORTA
 *
 * Sem ele, o campo é uma caixa preta. Um preenchimento automático do navegador pode
 * colocar o valor errado ali — aconteceu neste projeto: o Chrome preencheu o campo de
 * senha com o próprio e-mail, e o login falhava com "e-mail ou senha incorretos" sem
 * que houvesse como perceber que o campo estava com outro conteúdo.
 *
 * Revelar a senha é um risco de quem está ao lado olhando a tela, e por isso o padrão
 * é oculto e a revelação é um ato explícito. O que não se sustenta é não ter a opção.
 *
 * O botão é `type="button"`: sem isso ele submeteria o formulário.
 */

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  /** Marca o campo como inválido, para o contorno de erro. */
  invalido?: boolean;
};

export function CampoDeSenha({ invalido = false, className, ...resto }: Props) {
  const [revelada, definirRevelada] = useState(false);

  // Id próprio para o aria-controls, sem depender de quem usa o componente passar um.
  const idInterno = useId();
  const idDoCampo = resto.id ?? idInterno;

  return (
    <div className={estilos.moldura}>
      <input
        {...resto}
        id={idDoCampo}
        type={revelada ? 'text' : 'password'}
        className={[estilos.entrada, invalido ? estilos.entradaInvalida : '', className ?? '']
          .filter(Boolean)
          .join(' ')}
      />

      <button
        type="button"
        className={estilos.botao}
        onClick={() => {
          definirRevelada((atual) => !atual);
        }}
        aria-pressed={revelada}
        aria-controls={idDoCampo}
        /* O rótulo diz a AÇÃO, não o estado: é o que o leitor de tela anuncia ao
           chegar no botão, e "mostrar senha" responde o que o clique vai fazer. */
        aria-label={revelada ? 'Ocultar senha' : 'Mostrar senha'}
        title={revelada ? 'Ocultar senha' : 'Mostrar senha'}
      >
        {revelada ? <IconeOlhoFechado /> : <IconeOlhoAberto />}
      </button>
    </div>
  );
}

function IconeOlhoAberto() {
  return (
    <svg
      className={estilos.icone}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1.5 12S5 5.5 12 5.5 22.5 12 22.5 12 19 18.5 12 18.5 1.5 12 1.5 12z" />
      <circle cx="12" cy="12" r="3.2" />
    </svg>
  );
}

function IconeOlhoFechado() {
  return (
    <svg
      className={estilos.icone}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 3l18 18" />
      <path d="M10.6 6.1A9.9 9.9 0 0112 6c7 0 10.5 6 10.5 6a17.6 17.6 0 01-3.3 4" />
      <path d="M6.6 7.9A17.2 17.2 0 001.5 12S5 18 12 18a9.8 9.8 0 003.6-.7" />
      <path d="M9.9 9.9a3.2 3.2 0 004.3 4.3" />
    </svg>
  );
}
